import BreadCrumb from '@medical/components/Breadcrumb'
import Papa from 'papaparse'
import { Button } from 'primereact/button'
import React from 'react'
import Dropzone from 'react-dropzone'

import {
  CSV_COLUMNS,
  GROUP_STATUS,
  countRunnableGroups,
  normalizeCsvHeader,
} from './scheduledShiftGroupCsvUpload'

export const COLUMN_LABELS = {
  groupKey: 'グループ',
  name: 'シフトタイトル',
  email: '医師のメールアドレス',
  startDate: '開始日',
  endDate: '終了日',
  dayOfWeek: '曜日',
  weeksOfMonth: '毎月の第n回目',
  excludeHolidays: '祝日を除く',
  departmentCode: '診療科コード',
  startTime: '開始時間',
  splitDateTime1: '分割時間1',
  splitDateTime2: '分割時間2',
  splitDateTime3: '分割時間3',
  endTime: '終了時間',
  splitHourlyWage1: '分割時給1',
  splitHourlyWage2: '分割時給2',
  splitHourlyWage3: '分割時給3',
  hourlyWage: '時給',
  numberOfRooms: '募集数',
  comment: '業務内容',
  isSplitDateTime1BreakTime: '分割時間1休憩',
  isSplitDateTime2BreakTime: '分割時間2休憩',
  isSplitDateTime3BreakTime: '分割時間3休憩',
  isStartTimeBreakTime: '開始時間休憩',
  isPublished: '掲載/未掲載',
  isDoubleRecruitment: '二診目登録',
  delete: '削除',
}

const ACTION_LABELS = {
  CREATE: '作成',
  SKIP: '作成済み（飛ばす）',
  DELETE: '削除',
  ERROR: 'エラー',
}

// 削除の行で SKIP になるのは、途中で止まったアップロードで削除し終えていたとき
export const actionLabelOf = (plan, row) =>
  plan.action === 'SKIP' && String(row.delete || '').trim() === '1'
    ? '削除済み（飛ばす）'
    : ACTION_LABELS[plan.action]

const STATUS_LABELS = {
  [GROUP_STATUS.RUNNING]: '登録中…',
  [GROUP_STATUS.DONE]: '完了',
  [GROUP_STATUS.FAILED]: '失敗',
}

const isBlankRow = row =>
  Object.keys(row).every(
    key => row[key] == null || String(row[key]).trim() === ''
  )

// 行ごとに、その行が属するグループの計画を引けるようにする
export const planByRowIndex = checkResult => {
  const result = {}
  if (!checkResult) return result
  checkResult.groups.forEach(plan => {
    plan.rowIndexes.forEach(index => {
      result[index] = plan
    })
  })
  return result
}

export const summarizeUpload = ({ checkResult, statuses }) => {
  if (!checkResult) return null
  const total = countRunnableGroups(checkResult.groups)
  const done = Object.values(statuses).filter(
    s => s.status === GROUP_STATUS.DONE
  ).length
  const hasError = checkResult.groups.some(plan => plan.action === 'ERROR')
  return { total, done, hasError }
}

const ScheduledShiftGroupCsvScene = ({
  i18n,
  isRecruitment,
  isPermitted,
  rows,
  checkResult,
  statuses,
  isBusy,
  onLoadRows,
  onRemoveRow,
  onClear,
  onCheck,
  onUpload,
  downloadClinicalDepartments,
  downloadSample,
}) => {
  const [isParsing, setIsParsing] = React.useState(false)
  const title = isRecruitment
    ? '定期募集シフトCSVアップロード'
    : '定期確定シフトCSVアップロード'
  const columns = CSV_COLUMNS.filter(column =>
    rows.some(row => row[column] !== undefined)
  )
  const plans = planByRowIndex(checkResult)
  const summary = summarizeUpload({ checkResult, statuses })
  const canUpload =
    !!summary && !summary.hasError && summary.done < summary.total && !isBusy

  return (
    <React.Fragment>
      <div className='staff-header'>
        <div className='staff-title'>
          {title}
          <BreadCrumb items={[{ title }]} />
        </div>
        <div className='staff-buttons'>
          <Button
            icon='pi pi-download'
            className='p-button-normal'
            onClick={downloadClinicalDepartments}
            label={i18n.t('main.clinicalDepartment')}
          />
          <Button
            icon='pi pi-download'
            className='p-button-normal'
            onClick={downloadSample}
            label={i18n.t('main.sampleCsv')}
          />
        </div>
      </div>
      {isPermitted ? (
        <div className='container'>
          <div className='box'>
            <div className='box-top'>
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flex: 1,
                  alignItems: 'center',
                }}
              >
                {summary &&
                  (summary.hasError ? (
                    <div className='alert is-left'>
                      エラーのある行を直すか削除してから、もう一度チェックしてください。
                    </div>
                  ) : (
                    // 進み具合はエラーではないので、赤字（alert）にしない
                    <div>
                      {summary.total === 0
                        ? '登録するグループはありません（すべて作成済み・削除済みです）'
                        : `登録済み ${summary.done} / ${summary.total} グループ`}
                    </div>
                  ))}
              </div>
              {rows.length > 0 && (
                <Button
                  onClick={onClear}
                  label={i18n.t('main.cancel')}
                  className='p-button-secondary'
                  icon='pi pi-times'
                  style={{ marginRight: '20px' }}
                  disabled={isBusy}
                />
              )}
              <Button
                onClick={onCheck}
                label={i18n.t('staff.csvUpload.check')}
                disabled={rows.length === 0 || isBusy}
                style={{ marginRight: '20px' }}
              />
              <Button
                onClick={onUpload}
                label={i18n.t('staff.csvUpload.submit')}
                disabled={!canUpload}
              />
            </div>
            {rows.length < 1 && (
              <Dropzone
                onDrop={files => {
                  if (files.length > 0) {
                    setIsParsing(true)
                    Papa.parse(files[0], {
                      header: true,
                      skipEmptyLines: true,
                      transformHeader: normalizeCsvHeader,
                      complete: results => {
                        onLoadRows(results.data.filter(row => !isBlankRow(row)))
                        setIsParsing(false)
                      },
                      error: () => setIsParsing(false),
                    })
                  }
                }}
                disabled={isParsing}
                accept='.csv'
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps({ className: 'dropzone' })}>
                    <input {...getInputProps()} />
                    <p>
                      {isParsing
                        ? i18n.t('staff.csvUpload.parsing')
                        : i18n.t('doctor.uploadFile.dropFileHere')}
                    </p>
                  </div>
                )}
              </Dropzone>
            )}
            {rows.length > 0 && (
              <div className='overflow-container'>
                <table className='table'>
                  <thead>
                    <tr>
                      <th>{i18n.t('main.action')}</th>
                      <th>処理</th>
                      {columns.map(column => (
                        <th key={column}>{COLUMN_LABELS[column]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const plan = plans[index]
                      const rowResult =
                        checkResult &&
                        checkResult.rows.find(r => r.index === index)
                      const status = plan && statuses[plan.groupKey]
                      const errorMessage =
                        (rowResult && rowResult.errorMessage) ||
                        (status &&
                          status.status === GROUP_STATUS.FAILED &&
                          status.errorMessage)
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={index}>
                          <tr>
                            <td>
                              {rowResult && rowResult.errorMessage ? (
                                <Button
                                  onClick={() => onRemoveRow(index)}
                                  icon='pi pi-times'
                                  className='p-button-danger'
                                  disabled={isBusy}
                                />
                              ) : (
                                <Button
                                  icon={checkResult ? 'pi pi-check' : 'pi pi-info'}
                                  className={
                                    checkResult ? 'p-button-success' : undefined
                                  }
                                  tooltip={
                                    checkResult
                                      ? undefined
                                      : i18n.t('staff.csvUpload.notChecked')
                                  }
                                />
                              )}
                            </td>
                            <td>
                              {plan ? actionLabelOf(plan, row) : ''}
                              {status ? `（${STATUS_LABELS[status.status]}）` : ''}
                            </td>
                            {columns.map(column => (
                              <td key={column}>{row[column]}</td>
                            ))}
                          </tr>
                          {errorMessage && (
                            <tr>
                              <td colSpan={columns.length + 2}>
                                <div
                                  className='alert is-left'
                                  style={{ whiteSpace: 'pre-line' }}
                                >
                                  {errorMessage}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  )
}

export default ScheduledShiftGroupCsvScene
